import React, { useState, useEffect, useReducer, useContext } from "react";
import { getSocket } from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";

import IconButton from "@material-ui/core/IconButton";
import Snackbar from "@material-ui/core/Snackbar";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import NoteIcon from "@material-ui/icons/Note";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import { getLocale } from "../../translate/localeHelper";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
  canOpenTicket,
  noAccessMessage,
  pooledModeFromSetting,
} from "../../helpers/canOpenTicket";
import { Can } from "../../components/Can";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }
  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  notesTooltip: {
    maxWidth: 320,
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
}));

const NotesTooltip = ({ notes }) => {
  const classes = useStyles();
  if (!notes) return null;
  return (
    <Tooltip
      title={<Typography className={classes.notesTooltip}>{notes}</Typography>}
      placement="left"
      arrow
    >
      <NoteIcon style={{ fontSize: 16, color: "#9ca3af", cursor: "default" }} />
    </Tooltip>
  );
};

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const debouncedSearchParam = useDebouncedValue(searchParam, 300);
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);
  const [orderBy, setOrderBy] = useState("lastActivity");
  const [orderDir, setOrderDir] = useState("DESC");
  const [statusFilter, setStatusFilter] = useState("qualified");
  const [pendingCount, setPendingCount] = useState(0);
  const [undoSnackbar, setUndoSnackbar] = useState({ open: false, message: "", prevStatus: null, contactId: null, timeoutId: null });
  const [contextMenu, setContextMenu] = useState({ mouseX: null, mouseY: null, contact: null });

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(d => d === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(column);
      setOrderDir(column === "lastActivity" || column === "totalTickets" ? "DESC" : "ASC");
    }
    dispatch({ type: "RESET" });
    setPageNumber(1);
  };

  const STATUS_LABELS = {
    qualified: i18n.t("contacts.tabContactos"),
    pending: i18n.t("contacts.tabPendientes"),
    disqualified: i18n.t("contacts.tabDescartados"),
  };

  const handleMoveContact = async (contactId, newStatus, prevStatus) => {
    try {
      await api.put(`/contacts/${contactId}/status`, { status: newStatus });
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      if (undoSnackbar.timeoutId) clearTimeout(undoSnackbar.timeoutId);
      const tid = setTimeout(() => setUndoSnackbar(s => ({ ...s, open: false })), 6000);
      setUndoSnackbar({ open: true, message: i18n.t("contacts.movedToast", { tab: STATUS_LABELS[newStatus] }), prevStatus, contactId, timeoutId: tid });
    } catch (err) {
      toastError(err);
    }
  };

  const handleUndoMove = async () => {
    const { contactId, prevStatus, timeoutId } = undoSnackbar;
    if (timeoutId) clearTimeout(timeoutId);
    setUndoSnackbar(s => ({ ...s, open: false }));
    if (contactId && prevStatus) {
      try {
        await api.put(`/contacts/${contactId}/status`, { status: prevStatus });
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleContextMenu = (e, contact) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, contact });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ mouseX: null, mouseY: null, contact: null });
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [debouncedSearchParam]);

  const handleStatusFilter = (_, newStatus) => {
    setStatusFilter(newStatus);
    dispatch({ type: "RESET" });
    setPageNumber(1);
  };

  useEffect(() => {
    setLoading(true);
    const fetchContacts = async () => {
      try {
        const { data } = await api.get("/contacts/", {
          params: { searchParam: debouncedSearchParam, pageNumber, orderBy, orderDir, status: statusFilter },
        });
        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
        setHasMore(data.hasMore);
        if (data.totalContacts !== undefined) setTotalContacts(data.totalContacts);
        setLoading(false);
      } catch (err) {
        toastError(err);
      }
    };
    fetchContacts();
  }, [debouncedSearchParam, pageNumber, orderBy, orderDir, statusFilter]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const { data } = await api.get("/contacts/", {
          params: { status: "pending", pageNumber: 1 },
        });
        setPendingCount(data.count || 0);
      } catch (err) {}
    };
    fetchPendingCount();
  }, []);

  useEffect(() => {
    const socket = getSocket();
    // TicketsList y NotificationsPopOver (etapa 1) también escuchan "contact" sobre
    // este mismo singleton: el off() va POR REFERENCIA o los dejaría sordos.
    const handleContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
        // Refresh pending count on contact changes
        api.get("/contacts/", { params: { status: "pending", pageNumber: 1 } })
          .then(({ data: d }) => setPendingCount(d.count || 0))
          .catch(() => {});
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };
    socket.on("contact", handleContact);
    // SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
    return () => { socket.off("contact", handleContact); };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      // Un único check que respeta ticketReopenWindowDays: con chat continuo
      // (setting=0) devuelve también el ticket cerrado más reciente, así el asesor
      // vuelve al hilo del contacto en vez de arrancar uno nuevo. Reemplaza a las
      // dos consultas previas (status=open + status=pending), que ignoraban
      // `closed` y partían la conversación en cada cierre.
      const { data: active } = await api.get(`/contacts/${contactId}/active-ticket`);
      if (active?.exists) {
        // feat_337 — antes se navegaba SIN ningún gate: si el ticket no era
        // accesible se montaba el chat y ShowTicketService lo rebotaba con 404
        // (flash + cartel rojo). Ahora se decide acá y se avisa con un motivo.
        const verdict = canOpenTicket(active, user, {
          pooledMode: pooledModeFromSetting(
            getSetting("closedTicketsPooledByQueue")
          ),
        });
        if (verdict.ok) {
          history.push(`/tickets/${active.ticketId}`);
        } else {
          toast.warn(noAccessMessage(verdict), { autoClose: 10000 });
        }
        return;
      }
      // Sin ticket reutilizable → crear (el backend valida lo mismo)
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user?.id,
        status: "open",
      });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const [importing, setImporting] = useState(false);

  const handleimportContact = async () => {
    if (importing) return;
    setImporting(true);
    toast.info(i18n.t("contacts.importando"));
    try {
      const { data } = await api.post("/contacts/import");
      const n = data?.imported ?? null;
      const s = data?.skipped ?? null;
      if (n !== null) {
        toast.success(i18n.t("contacts.importOk", { imported: n, skipped: s ?? 0 }));
      } else {
        toast.success(i18n.t("contacts.importDone"));
      }
      history.go(0);
    } catch (err) {
      toastError(err);
    } finally {
      setImporting(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const SortableHeader = ({ id, label, align = "left" }) => (
    <TableCell align={align} style={{ cursor: "pointer", userSelect: "none" }}>
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? orderDir.toLowerCase() : "asc"}
        onClick={() => handleSort(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact ? handleDeleteContact(deletingContact.id) : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <MainHeader>
        <Title>
          {i18n.t("contacts.title")}
          {totalContacts > 0 && (
            <Chip
              label={totalContacts}
              size="small"
              style={{ marginLeft: 8, fontWeight: 700, fontSize: 11 }}
            />
          )}
        </Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" color="primary" onClick={() => setConfirmOpen(true)}>
            {i18n.t("contacts.buttons.import")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleOpenContactModal}>
            {i18n.t("contacts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Tabs
          value={statusFilter}
          onChange={handleStatusFilter}
          indicatorColor="primary"
          textColor="primary"
          style={{ borderBottom: "1px solid #e0e0e0", marginBottom: 4 }}
        >
          <Tab label={i18n.t("contacts.tabContactos")} value="qualified" />
          <Tab label={<Badge badgeContent={pendingCount} color="secondary" style={{ paddingRight: pendingCount > 0 ? 12 : 0 }}>{i18n.t("contacts.tabPendientes")}</Badge>} value="pending" />
          <Tab label={i18n.t("contacts.tabDescartados")} value="disqualified" />
          <Tab label={i18n.t("contacts.tabTodos")} value="all" />
        </Tabs>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <SortableHeader id="name" label={i18n.t("contacts.table.name")} />
              <TableCell align="center">{i18n.t("contacts.colNombreWA")}</TableCell>
              <SortableHeader id="contactType" label={i18n.t("contacts.colTipoCliente")} align="center" />
              <SortableHeader id="email" label={i18n.t("contacts.table.email")} align="center" />
              <TableCell align="center">{i18n.t("contacts.colNumero")}</TableCell>
              <SortableHeader id="organization" label={i18n.t("contacts.colEmpresa")} align="center" />
              <TableCell align="center">{i18n.t("contacts.colNotas")}</TableCell>
              <SortableHeader id="attendedBy" label={i18n.t("contacts.colAtendidoPor")} align="center" />
              <SortableHeader id="totalTickets" label={i18n.t("contacts.colTotalChats")} align="center" />
              <SortableHeader id="lastActivity" label={i18n.t("contacts.colUltimaActividad")} align="center" />
              <TableCell align="center">{i18n.t("contacts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell style={{ paddingRight: 0 }}>
                    <Avatar src={contact.profilePicUrl} />
                  </TableCell>
                  <TableCell>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {/* feat_305 — mismo helper que el zócalo y el header.
                          fallback:null preserva el "Sin nombre" en itálica.
                          La columna Empresa se mantiene (es ordenable). */}
                      {getContactDisplayName(contact, { fallback: null }) || (
                        <span style={{ color: "#999", fontStyle: "italic" }}>{i18n.t("contacts.sinNombre")}</span>
                      )}
                      {contact.aiBlocked && (
                        <Chip
                          label="🚫 IA"
                          size="small"
                          style={{ height: 16, fontSize: 9, backgroundColor: "#9c27b0", color: "#fff" }}
                        />
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {contact.whatsappName ? contact.whatsappName : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {contact.contactType ? (
                      <Chip
                        label={contact.contactType.name}
                        size="small"
                        style={{ backgroundColor: contact.contactType.color, color: "#fff", fontSize: 11 }}
                      />
                    ) : "—"}
                  </TableCell>
                  <TableCell align="center">{contact.email || "—"}</TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">{contact.organization || "—"}</TableCell>
                  <TableCell align="center">
                    <NotesTooltip notes={contact.notes} />
                  </TableCell>
                  <TableCell align="center">{contact.tickets?.[0]?.user?.name || "—"}</TableCell>
                  <TableCell align="center">{contact.totalTickets ?? "—"}</TableCell>
                  <TableCell align="center">
                    {contact.lastActivity
                      ? new Date(contact.lastActivity).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "2-digit" })
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleSaveTicket(contact.id)}>
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => hadleEditContact(contact.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => handleContextMenu(e, contact)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </>
          </TableBody>
        </Table>
      </Paper>

      {/* Context menu — move contact between tabs */}
      <Menu
        open={contextMenu.mouseY !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.mouseY !== null && contextMenu.mouseX !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu.contact && ["qualified", "pending", "disqualified"].filter(s => s !== contextMenu.contact?.status).map(s => (
          <MenuItem
            key={s}
            onClick={() => {
              handleMoveContact(contextMenu.contact.id, s, contextMenu.contact.status);
              handleCloseContextMenu();
            }}
          >
            {i18n.t(`contacts.menuMoveTo_${s}`)}
          </MenuItem>
        ))}
      </Menu>

      {/* Undo snackbar */}
      <Snackbar
        open={undoSnackbar.open}
        message={undoSnackbar.message}
        action={
          <Button color="secondary" size="small" onClick={handleUndoMove}>
            {i18n.t("contacts.undoButton")}
          </Button>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </MainContainer>
  );
};

export default Contacts;
